<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { VueFlow, useVueFlow, Panel } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import FlowsAPI from 'dashboard/api/flows';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FlowNode from './components/FlowNode.vue';
import FlowPalette from './components/FlowPalette.vue';
import FlowConfigPanel from './components/FlowConfigPanel.vue';
import { nodeDefinition } from './flowNodeCatalog';
import {
  defaultGraph,
  graphPayload,
  numberOrNull,
  summarize,
  toVueNode,
  validationError,
} from './flowGraph';

const NODE_TYPES = { flowNode: FlowNode };
const DEFAULT_TRIGGER_MINUTES = 60;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { fitView } = useVueFlow();

const flowId = ref(route.params.flowId);
const flowName = ref('');
const flowStatus = ref('draft');
const flowSettings = ref({
  trigger_type: 'no_reply',
  trigger_minutes: DEFAULT_TRIGGER_MINUTES,
  trigger_stage: '',
  daily_message_cap: null,
  send_window_start_hour: null,
  send_window_end_hour: null,
});
const nodes = ref([]);
const edges = ref([]);
const selectedNode = ref(null);
const isFetching = ref(false);
const isSaving = ref(false);
const isPublishing = ref(false);
const isPausing = ref(false);
const simulation = ref(null);
const activeMobilePanel = ref(null);

const isNewFlow = computed(() => flowId.value === 'new');
const statusLabel = computed(() => {
  if (flowStatus.value === 'published') return t('FLOWS.BUILDER.PUBLISHED');
  if (flowStatus.value === 'paused') return t('FLOWS.BUILDER.PAUSED');

  return t('FLOWS.BUILDER.DRAFT');
});
const statusClasses = computed(() => {
  if (flowStatus.value === 'published') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (flowStatus.value === 'paused') {
    return 'bg-slate-100 text-slate-700 border-slate-200';
  }

  return 'bg-amber-50 text-amber-700 border-amber-200';
});

const loadGraph = graph => {
  const apiGraph = graph?.nodes?.length ? graph : defaultGraph(t);
  nodes.value = apiGraph.nodes.map((node, index) => toVueNode(t, node, index));
  edges.value = (apiGraph.edges || []).map(edge => ({
    id: edge.id || `${edge.source}-${edge.target}-${edge.branch || 'default'}`,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.branch || null,
  }));
  nextTick(() => fitView({ padding: 0.25 }));
};

const fetchFlow = async () => {
  if (isNewFlow.value) {
    flowName.value = t('FLOWS.BUILDER.DEFAULT_NAME');
    loadGraph(defaultGraph(t));
    return;
  }

  isFetching.value = true;
  try {
    const { data } = await FlowsAPI.getFlow(flowId.value);
    flowName.value = data.name;
    flowStatus.value = data.status;
    flowSettings.value = {
      trigger_type: data.trigger_config?.type || 'no_reply',
      trigger_minutes:
        data.trigger_config?.delay_minutes || DEFAULT_TRIGGER_MINUTES,
      trigger_stage: data.trigger_config?.stage || '',
      daily_message_cap: data.daily_message_cap || null,
      send_window_start_hour: data.send_window_start_hour ?? null,
      send_window_end_hour: data.send_window_end_hour ?? null,
    };
    loadGraph(data.graph);
  } catch {
    useAlert(t('FLOWS.BUILDER.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const updateNodeConfig = updatedNode => {
  const node = {
    ...updatedNode,
    data: {
      ...updatedNode.data,
      summary: summarize(t, {
        type: updatedNode.data.kind,
        config: updatedNode.data.config,
      }),
    },
  };

  if (node.data.kind === 'switch') {
    const branchKeys = new Set(
      (node.data.config?.cases || []).map(flowCase => flowCase.branch)
    );
    edges.value = edges.value.filter(
      edge =>
        edge.source !== node.id ||
        !edge.sourceHandle ||
        branchKeys.has(edge.sourceHandle)
    );
  }

  nodes.value = nodes.value.map(candidate =>
    candidate.id === node.id ? node : candidate
  );
  selectedNode.value = node;
};

const updateFlowSettings = settings => {
  flowSettings.value = settings;
};

const onNodeClick = ({ node }) => {
  selectedNode.value = node;
  if (window.matchMedia('(max-width: 1023px)').matches) {
    activeMobilePanel.value = 'config';
  }
};

const onPaneClick = () => {
  selectedNode.value = null;
  activeMobilePanel.value = null;
};

const toggleMobilePanel = panel => {
  activeMobilePanel.value = activeMobilePanel.value === panel ? null : panel;
};

const onConnect = connection => {
  const id = `${connection.source}-${connection.target}-${
    connection.sourceHandle || 'default'
  }`;
  if (edges.value.some(edge => edge.id === id)) return;

  edges.value = [
    ...edges.value,
    {
      id,
      source: connection.source,
      target: connection.target,
      sourceHandle: connection.sourceHandle,
    },
  ];
};

const addNode = kind => {
  const definition = nodeDefinition(t, kind);
  const id = `${kind}-${Date.now()}`;
  const position = {
    x: 100 + (nodes.value.length % 4) * 250,
    y: 100 + Math.floor(nodes.value.length / 4) * 170,
  };
  const node = {
    id,
    type: 'flowNode',
    position,
    data: {
      kind,
      label: definition.label,
      summary: summarize(t, { type: kind, config: definition.config }),
      config: definition.config,
      yesLabel: t('FLOWS.CONDITION.YES'),
      noLabel: t('FLOWS.CONDITION.NO'),
    },
  };
  nodes.value = [...nodes.value, node];
  selectedNode.value = node;
};

const deleteNode = nodeId => {
  nodes.value = nodes.value.filter(node => node.id !== nodeId);
  edges.value = edges.value.filter(
    edge => edge.source !== nodeId && edge.target !== nodeId
  );
  selectedNode.value = null;
};

const saveFlow = async ({ publish = false } = {}) => {
  const error = validationError({
    t,
    name: flowName.value,
    nodes: nodes.value,
    edges: edges.value,
    settings: flowSettings.value,
  });
  if (error) {
    useAlert(error);
    return;
  }

  isSaving.value = true;
  if (publish) isPublishing.value = true;

  try {
    const payload = {
      name: flowName.value.trim(),
      daily_message_cap: numberOrNull(flowSettings.value.daily_message_cap),
      send_window_start_hour: numberOrNull(
        flowSettings.value.send_window_start_hour
      ),
      send_window_end_hour: numberOrNull(
        flowSettings.value.send_window_end_hour
      ),
      trigger_config: {
        type: flowSettings.value.trigger_type,
        delay_minutes: Number(flowSettings.value.trigger_minutes),
        stage: flowSettings.value.trigger_stage || null,
      },
      graph: graphPayload(nodes.value, edges.value),
    };

    let savedFlow;
    if (isNewFlow.value) {
      const { data } = await FlowsAPI.createFlow(payload);
      savedFlow = data;
      flowId.value = data.id;
      flowStatus.value = data.status;
      router.replace(accountScopedRoute('flow_builder', { flowId: data.id }));
    } else {
      const { data } = await FlowsAPI.updateFlow(flowId.value, payload);
      savedFlow = data;
      flowStatus.value = data.status;
    }

    if (publish) {
      const { data } = await FlowsAPI.publishFlow(savedFlow.id);
      flowStatus.value = data.status;
    }

    useAlert(
      publish
        ? t('FLOWS.BUILDER.PUBLISHED_MESSAGE')
        : t('FLOWS.BUILDER.SAVED_MESSAGE')
    );
  } catch {
    useAlert(t('FLOWS.BUILDER.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
    isPublishing.value = false;
  }
};

const goBack = () => router.push(accountScopedRoute('flows_list'));

const togglePause = async () => {
  if (isPausing.value) return;

  isPausing.value = true;
  try {
    const { data } =
      flowStatus.value === 'paused'
        ? await FlowsAPI.resumeFlow(flowId.value)
        : await FlowsAPI.pauseFlow(flowId.value);
    flowStatus.value = data.status;
    useAlert(
      flowStatus.value === 'paused'
        ? t('FLOWS.BUILDER.PAUSED_MESSAGE')
        : t('FLOWS.BUILDER.RESUMED_MESSAGE')
    );
  } catch {
    useAlert(t('FLOWS.BUILDER.SAVE_ERROR'));
  } finally {
    isPausing.value = false;
  }
};

// Pre-visualizacao estatica: anda do start seguindo a primeira saida de cada
// bloco, sem enviar nada. Serve para revisar o caminho antes de ativar.
const runSimulation = () => {
  const steps = [];
  const visited = new Set();
  let current = nodes.value.find(node => node.data.kind === 'start');

  while (current && !visited.has(current.id)) {
    const currentId = current.id;
    visited.add(currentId);
    steps.push({
      id: currentId,
      label: current.data.label,
      summary: current.data.summary,
    });
    if (current.data.kind === 'end') break;

    const next = edges.value.find(edge => edge.source === currentId);
    current = next ? nodes.value.find(node => node.id === next.target) : null;
  }

  simulation.value = steps;
};

const closeSimulation = () => {
  simulation.value = null;
};

onMounted(fetchFlow);
</script>

<template>
  <section class="flex flex-col flex-1 min-h-0 bg-n-background">
    <header
      class="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-n-weak bg-n-solid-1"
    >
      <Button
        size="sm"
        color="slate"
        icon="i-lucide-arrow-left"
        @click="goBack"
      >
        {{ t('FLOWS.BUILDER.BACK') }}
      </Button>
      <Button
        v-tooltip="t('FLOWS.PALETTE.TITLE')"
        class="lg:hidden"
        size="sm"
        color="slate"
        icon="i-lucide-blocks"
        :aria-label="t('FLOWS.PALETTE.TITLE')"
        @click="toggleMobilePanel('palette')"
      />
      <Button
        v-tooltip="t('FLOWS.CONFIG.TITLE')"
        class="lg:hidden"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-horizontal"
        :aria-label="t('FLOWS.CONFIG.TITLE')"
        @click="toggleMobilePanel('config')"
      />
      <Input
        v-model="flowName"
        class="w-64"
        :placeholder="t('FLOWS.BUILDER.NAME_PLACEHOLDER')"
      />
      <span
        class="px-2.5 py-1 text-xs font-medium border rounded-full"
        :class="statusClasses"
      >
        {{ statusLabel }}
      </span>
      <span class="flex-1" />
      <Button
        size="sm"
        color="slate"
        icon="i-lucide-flask-conical"
        @click="runSimulation"
      >
        {{ t('FLOWS.BUILDER.TEST') }}
      </Button>
      <Button
        v-if="!isNewFlow"
        size="sm"
        color="slate"
        :icon="flowStatus === 'paused' ? 'i-lucide-play' : 'i-lucide-pause'"
        :is-loading="isPausing"
        @click="togglePause"
      >
        {{
          flowStatus === 'paused'
            ? t('FLOWS.BUILDER.RESUME')
            : t('FLOWS.BUILDER.PAUSE')
        }}
      </Button>
      <Button
        size="sm"
        color="slate"
        :is-loading="isSaving && !isPublishing"
        @click="saveFlow()"
      >
        {{ t('FLOWS.BUILDER.SAVE') }}
      </Button>
      <Button
        size="sm"
        :is-loading="isPublishing"
        @click="saveFlow({ publish: true })"
      >
        {{ t('FLOWS.BUILDER.PUBLISH') }}
      </Button>
    </header>

    <div v-if="isFetching" class="flex items-center justify-center flex-1">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else class="flex flex-1 min-h-0">
      <button
        v-if="activeMobilePanel"
        type="button"
        class="fixed inset-0 z-20 bg-n-alpha-black2 lg:hidden"
        :aria-label="t('FLOWS.BUILDER.CLOSE_PANEL')"
        @click="activeMobilePanel = null"
      />

      <div
        :class="
          activeMobilePanel === 'palette'
            ? 'fixed inset-y-0 start-0 z-30 flex w-64 lg:contents'
            : 'hidden lg:contents'
        "
      >
        <FlowPalette @add-node="addNode" />
      </div>

      <main class="relative flex-1 min-w-0 bg-n-background">
        <div
          v-if="simulation"
          class="absolute z-10 max-w-sm p-4 m-4 border shadow-lg start-4 top-4 rounded-2xl border-n-weak bg-n-solid-1"
        >
          <div class="flex items-start justify-between gap-3">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ t('FLOWS.BUILDER.SIMULATION_TITLE') }}
            </p>
            <button
              type="button"
              class="text-n-slate-10 hover:text-n-slate-12"
              :aria-label="t('FLOWS.BUILDER.CLOSE_PANEL')"
              @click="closeSimulation"
            >
              <span class="i-lucide-x" />
            </button>
          </div>
          <p class="mt-1 text-xs leading-5 text-n-slate-10">
            {{ t('FLOWS.BUILDER.SIMULATION_HINT') }}
          </p>
          <ol v-if="simulation.length" class="mt-3 space-y-2">
            <li
              v-for="(step, index) in simulation"
              :key="step.id"
              class="flex gap-2 text-xs text-n-slate-11"
            >
              <span class="font-medium text-n-slate-12">
                {{ `${index + 1}.` }}
              </span>
              <span class="min-w-0">
                <span class="block font-medium text-n-slate-12">
                  {{ step.label }}
                </span>
                <span v-if="step.summary" class="block truncate">
                  {{ step.summary }}
                </span>
              </span>
            </li>
          </ol>
          <p v-else class="mt-3 text-xs text-n-slate-10">
            {{ t('FLOWS.BUILDER.SIMULATION_EMPTY') }}
          </p>
        </div>
        <VueFlow
          v-model:nodes="nodes"
          v-model:edges="edges"
          :node-types="NODE_TYPES"
          :min-zoom="0.25"
          :max-zoom="1.8"
          fit-view-on-init
          @connect="onConnect"
          @node-click="onNodeClick"
          @pane-click="onPaneClick"
        >
          <Background
            :gap="20"
            pattern-color="currentColor"
            class="text-n-slate-4"
          />
          <Controls position="bottom-left" />
          <MiniMap position="bottom-right" pannable zoomable />
          <Panel position="top-left" class="max-w-xs">
            <div
              class="px-3 py-2 text-xs leading-5 border rounded-xl border-n-weak bg-n-solid-1/90 text-n-slate-11 backdrop-blur"
            >
              {{ t('FLOWS.BUILDER.CANVAS_HINT') }}
            </div>
          </Panel>
        </VueFlow>
      </main>

      <div
        :class="
          activeMobilePanel === 'config'
            ? 'fixed inset-y-0 end-0 z-30 flex w-80 lg:contents'
            : 'hidden lg:contents'
        "
      >
        <FlowConfigPanel
          :node="selectedNode"
          :flow-settings="flowSettings"
          @update-node-config="updateNodeConfig"
          @update-flow-settings="updateFlowSettings"
          @delete-node="deleteNode"
        />
      </div>
    </div>
  </section>
</template>
